import React from "react";

import { DatePicker, SectionCard, Select } from "@amphore-dev/amphore-lib";
import { useFormikContext } from "formik";
import i18n, { t } from "i18next";

import { ClientSelect, CompanySelect } from "@/components/molecules";

import { INVOICE_CURRENCIES } from "@/constants/CInvoices";

import { TCompany, TInvoiceFormValues } from "@/types";

import { dateLocaleFor } from "@/i18n/dateLocale";
import { dateFromApi, dateToApi, dueDateFor } from "@/utils";

import { useInvoiceForm } from "./InvoiceFormContext";

/** Issuer, client, currency and the two dates. */
export const InvoiceHeaderCard: React.FC<{ className?: string }> = ({
	className,
}) => {
	const { values, setFieldValue, errors, touched } =
		useFormikContext<TInvoiceFormValues>();
	const { isLocked, isBusy, billing, existing } = useInvoiceForm();
	const disabled = isLocked || isBusy;
	const locale = dateLocaleFor(i18n.language);

	// The currency select offers a short list; an invoice saved in
	// another one keeps it as an extra option.
	const currencies = INVOICE_CURRENCIES.includes(
		values.currency as (typeof INVOICE_CURRENCIES)[number]
	)
		? [...INVOICE_CURRENCIES]
		: [values.currency, ...INVOICE_CURRENCIES];

	return (
		<SectionCard
			title={
				<div className="flex items-center gap-3">
					<span>{t("invoices.form.header")}</span>
					{existing?.number && (
						<span className="font-normal text-neutral-500">
							{existing.number}
						</span>
					)}
				</div>
			}
			className={className}
		>
			{existing?.report && (
				<p className="mb-4 text-sm text-neutral-500">
					{t("invoices.form.fromReport", {
						name: existing.report.name,
					})}
				</p>
			)}
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<CompanySelect
					label={t("invoices.form.company")}
					value={values.company as TCompany | null}
					onChange={(value) =>
						setFieldValue("company", value ?? null)
					}
					disabled={disabled}
					isClearable={false}
					error={
						touched.company && errors.company
							? String(errors.company)
							: undefined
					}
				/>
				<ClientSelect
					label={t("invoices.form.client")}
					value={values.client as TCompany | null}
					onChange={(value) => setFieldValue("client", value ?? null)}
					disabled={disabled}
				/>
				<Select<string>
					label={t("invoices.form.currency")}
					options={currencies.map((code) => ({
						value: code,
						label: code,
					}))}
					value={values.currency}
					onChange={(value) =>
						setFieldValue(
							"currency",
							(Array.isArray(value) ? value[0] : value) ??
								billing?.defaultCurrency ??
								"EUR"
						)
					}
					disabled={disabled}
				/>
				<div className="grid grid-cols-2 gap-4">
					<DatePicker
						label={t("invoices.form.issuedAt")}
						value={dateFromApi(values.issuedAt)}
						onChange={(date) => {
							const issuedAt = dateToApi(date);
							setFieldValue("issuedAt", issuedAt);
							// The due date follows the issue date while the user
							// has not set one of their own.
							if (issuedAt && !values.dueAt) {
								setFieldValue(
									"dueAt",
									dueDateFor(
										issuedAt,
										billing?.paymentTermsDays ?? 30
									)
								);
							}
						}}
						locale={locale}
						disabled={disabled}
						portal
					/>
					<DatePicker
						label={t("invoices.form.dueAt")}
						value={dateFromApi(values.dueAt)}
						onChange={(date) =>
							setFieldValue("dueAt", dateToApi(date))
						}
						locale={locale}
						disabled={disabled}
						portal
					/>
				</div>
			</div>
		</SectionCard>
	);
};
